import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, EnvelopeSimple } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1641760378661-6f290a50a62d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBza3lzY3JhcGVyJTIwZ2xhc3N8ZW58MHx8fHwxNzc4NDIxMzk5fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("exec.blackrock@vlink.demo");
  const [password, setPassword] = useState("Demo1234!");
  const [error, setError] = useState("");

  if (user) return <Navigate to={loc.state?.from || "/"} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      nav(loc.state?.from || "/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 h-screen bg-white" data-testid="login-page">
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-black/40 backdrop-blur-sm border border-white/10 flex items-center justify-center rounded-sm">
                <img src="/vlink-logo.png" alt="VLink" className="w-12 h-12 object-contain" />
              </div>
              <div>
                <div className="font-heading text-2xl tracking-tight">Workflow Assurance<span className="text-vlink-400">.</span></div>
                <div className="overline text-white/50 mt-0.5">An AI Control Plane</div>
              </div>
            </div>
          </div>
          <div className="max-w-2xl">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-heading text-5xl xl:text-6xl tracking-tight leading-[1.05]"
            >
              The defect that survives quarterly UAT becomes Monday morning's
              <span className="text-vlink-400"> $40M call.</span>
            </motion.h2>
            <p className="mt-6 text-white/70 text-base max-w-lg leading-relaxed">
              An AI-driven control plane for fragmented QA across asset managers, custodians,
              and capital-markets platforms. Workflow Intelligence → Defect Prediction → Release Confidence.
            </p>
            <p className="mt-4 text-white/40 text-xs font-mono uppercase tracking-widest">
              One framework · Many clients · BlackRock · Morgan Stanley · State Street · Goldman Sachs
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-2xl pt-8 border-t border-white/10">
            <div>
              <div className="metric-value text-3xl">4<span className="text-white/40 text-sm font-mono ml-1">clients</span></div>
              <div className="overline text-white/50 mt-1">Multi-tenant</div>
            </div>
            <div>
              <div className="metric-value text-3xl">7<span className="text-white/40 text-sm font-mono ml-1">layers</span></div>
              <div className="overline text-white/50 mt-1">Unified stack</div>
            </div>
            <div>
              <div className="metric-value text-3xl text-vlink-400">AI<span className="text-white/40 text-sm font-mono ml-1">native</span></div>
              <div className="overline text-white/50 mt-1">Claude 4.5</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <div className="overline text-zinc-500 mb-4">Sign in</div>
          <h1 className="font-heading text-3xl tracking-tight text-zinc-900">Access the platform.</h1>
          <p className="mt-2 text-sm text-zinc-600">Enter your credentials to continue.</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="overline text-zinc-500 mb-2 block">Email</label>
              <div className="flex items-center border border-zinc-300 focus-within:border-vlink-500 rounded-sm bg-white">
                <EnvelopeSimple size={16} className="text-zinc-400 ml-3" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
                  required
                />
              </div>
            </div>
            <div>
              <label className="overline text-zinc-500 mb-2 block">Password</label>
              <div className="flex items-center border border-zinc-300 focus-within:border-vlink-500 rounded-sm bg-white">
                <Lock size={16} className="text-zinc-400 ml-3" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
                  required
                />
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Continue"} <ArrowRight size={14} />
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-zinc-200">
            <div className="overline text-zinc-500 mb-3">Demo accounts</div>
            <div className="space-y-2 text-xs font-mono" data-testid="demo-creds">
              <div>
                <div className="text-zinc-400 text-[10px] uppercase tracking-widest mb-0.5">VLink Executive (sees all)</div>
                <div className="text-zinc-700">exec@vlink.demo</div>
              </div>
              <div>
                <div className="text-zinc-400 text-[10px] uppercase tracking-widest mb-0.5">Client Executive</div>
                <div className="text-zinc-700">exec.blackrock@vlink.demo</div>
                <div className="text-zinc-700">exec.goldman@vlink.demo</div>
              </div>
              <div>
                <div className="text-zinc-400 text-[10px] uppercase tracking-widest mb-0.5">Client Manager</div>
                <div className="text-zinc-700">mgr.blackrock@vlink.demo</div>
                <div className="text-zinc-700">mgr.statestreet@vlink.demo</div>
              </div>
              <div className="text-zinc-500 pt-1">password · Demo1234!</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
